import React, { useEffect, useState, useRef } from 'react'
import Icons from '../../Icons'
import axios from '../../../../data/http/axios'

// Display chat bubbles in a conversation

export default function ChatMessage({
    sent,
    note,
    className,
    message,
    isMe = false,
    isAi = false,
    isAttachment = false,
    attachment,
    size,
    draft = false,
    type,
    download = () => {}
}) {
    const timer = useRef(null)
    const [attachmentStatus, setAttachmentStatus] = useState(attachment?.status)
    const [refreshTimeouts, setRefreshTimeouts] = useState(0)
    const [location, setLocation] = useState(attachment?.link || attachment?.location)

    let classes = ''

    if (isMe && !isAi) {
        classes += ' chat-message--is-me'
    }

    if (isAi) {
        classes += ' chat-message--is-ai'
    }

    if (note) {
        classes += ' type--is-note'
    }

    if (isAttachment) {
        classes += ' chat-message--is-attachment'
    }

    if (draft) {
        classes += ' chat-message--is-draft'
    }

    useEffect(() => {
        if (!attachmentStatus) return
        if (attachmentStatus === 1 || attachmentStatus === 3) {
            timer.current = setTimeout(() => {
                setRefreshTimeouts((t) => t + 1)
                if (refreshTimeouts < 10) {
                    axios.get(`/inboxes/chat/${attachment.ChatId}/attachment/${attachment.id}`).then(({ data }) => {
                        setAttachmentStatus(data.status)
                        if (data.location) {
                            setLocation(data.location)
                        }
                    })
                } else {
                    setAttachmentStatus(5)
                }
            }, 2500)
        }
    }, [attachmentStatus, refreshTimeouts])

    const refreshAttachment = () => {
        setRefreshTimeouts(0)
        setAttachmentStatus(1)
    }

    if (isAttachment) {
        let icon = <Icons icon='download3' color='#453DD8' style={{ height: 30, width: 30, cursor: 'pointer' }} />
        if (attachmentStatus === 1) {
            // awaiting antivirus scan
            icon = <Icons icon='loading' color='var(--indigo-600)' style={{ height: 25, width: 25 }} />
        }
        if (attachmentStatus === 2) {
            // error when scanning
            icon = <Icons icon='alert' color='var(--red-600)' style={{ height: 25, width: 25 }} />
        }
        if (attachmentStatus === 3) {
            // scan in progress
            icon = <Icons icon='loading' color='var(--indigo-600)' style={{ height: 25, width: 25 }} />
        }
        if (attachmentStatus === 4) {
            // success
            // icon = <Icons icon='check' color='var(--green-600)' style={{ height: 25, width: 25 }} />
        }
        if (attachmentStatus === 5) {
            // timeout, allow manual refresh
            icon = (
                <Icons icon='replace' color='var(--indigo-600)' style={{ height: 25, width: 25, cursor: 'pointer' }} />
            )
        }
        if (attachmentStatus === 99) {
            // virus detected
            icon = <Icons icon='alert' color='var(--red-600)' style={{ height: 25, width: 25 }} />
        }
        if ((type.indexOf('image/') === 0 && location) || (type === 'image' && location)) {
            const imageStyle = {
                height: 30,
                width: 30,
                backgroundSize: 'cover',
                backgroundPositionX: 'center',
                backgroundPositionY: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundImage: `url(${location})`,
                cursor: attachmentStatus === 4 || attachmentStatus === 5 ? 'pointer' : 'default',
                borderRadius: '5px'
            }
            icon = <div alt='attachment' style={imageStyle} />
        }

        let clickClasses = 'message__attachment__icon '
        if (attachmentStatus === 2 || attachmentStatus === 99) clickClasses += 'message__attachment__icon--error'
        if (attachmentStatus === 5) {
            onClickDownload = refreshAttachment
        }

        let onClickDownload
        if (attachmentStatus === 4) onClickDownload = () => download(location, attachment)
        if ((type === 'image' || type === 'file') && location) {
            // facebook so allow
            onClickDownload = () => download(location, attachment)
            if (type === 'file') message = location.split('/')[location.split('/').length - 1].split('?')[0]
            else message = ''
        }
        if (type === 'audio') {
            // facebook voice, play inline
            message = (
                <audio controls preload='none' style={{ height: 50, width: 300, verticalAlign: 'middle' }}>
                    <source src={location} type='audio/mpeg' />
                    Your browser does not support the audio element.
                </audio>
            )
            icon = ''
            classes += ' __audio'
        }
        if (type === 'video') {
            // facebook video, play inline
            message = (
                <video
                    controls
                    preload='none'
                    style={{ height: 200, width: 300, verticalAlign: 'middle', borderRadius: 8 }}
                >
                    <source src={location} type='video/mp4' />
                    Your browser does not support the video element.
                </video>
            )
            icon = ''
            classes += ' __video'
        }

        let explainer = size ? humanizeBytes(size) : ''
        if (attachmentStatus === 3 || attachmentStatus === 1) {
            explainer = 'Scanning...'
        }
        if (attachmentStatus === 2) {
            explainer = attachment.error
        }
        if (attachmentStatus === 5) {
            explainer = 'Click to refresh...'
        }
        if (attachmentStatus === 99) {
            explainer = 'Virus detected'
        }

        return (
            <div className={`chat-message ${classes}`}>
                <div className='chat-message__attachment'>
                    {!!icon && (
                        <div className={clickClasses} onClick={onClickDownload}>
                            {icon}
                        </div>
                    )}
                    <div className='chat-message__attachment__info'>
                        <div>{message}</div>
                        {size !== null && size !== undefined && (
                            <div className='chat-message__attachment__info__size'>{explainer}</div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            {isAi && (
                <div className='chat-message__ai-indicator'>
                    <span>AI</span>Answer
                </div>
            )}
            {draft && <div className='chat-message__draft-indicator'>Draft</div>}
            <p className={`chat-message ${classes}`} dangerouslySetInnerHTML={{ __html: message }}></p>
        </>
    )
}

function humanizeBytes(bytes) {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
    if (bytes === 0) return '0 Byte'
    const i = Math.floor(Math.log(bytes) / Math.log(1024))
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i]
}
